import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { useCurrency } from "../../utils";
import { asociarPagosCapex, desasociarPagosCapex, getAsociacionActiva } from "../../utils/capex/asociarPagosCapex";
import { getPagosCapexCandidatos } from "../../utils/capex/pagosCapexCandidatos";
import { client } from "../../utils/supabase";

// Tolerancia de cuadre en moneda local (misma doctrina que TOLERANCIA_REDONDEO_LOCAL del ledger):
// el reparto de un activo en cuotas deja migas de redondeo (caso real INNOVA: Σ 2.332.401 vs total
// 2.332.400 → 1 peso). Habilita Confirmar cuando la selección CUBRE el total dentro de la tolerancia.
const TOLERANCIA_CUADRE_LOCAL = 10;

/**
 * Modal COMPARTIDO (regla 10.1) — "Asociar pagos de CapEx a esta factura".
 *
 * Se abre desde DOS lugares con el mismo componente: el <KebabMenu> de la fila en Compras
 * (ComprasTab) y la fila de la factura en Movimientos futuros → Por pagar (FlujoCajaV2). Recibe
 * SOLO los identificadores y busca los datos de la factura él mismo, para no depender del shape
 * de cada punto de entrada (un doc normalizado vs. una entry de proyección).
 *
 * DOS estados:
 *   · Factura SIN asociación → lista los pagos CapEx candidatos (huérfanos, doble candado) y permite
 *     Confirmar (escritura atómica vía RPC asociar_pagos_capex_a_factura).
 *   · Factura CON asociación activa → muestra los pagos asociados y permite Desasociar (RPC reversa).
 *
 * @param {boolean}  show
 * @param {Function} onClose
 * @param {Function} [onSuccess] - se llama tras asociar/desasociar (para que el padre recargue).
 * @param {number}   facturaId   - id en facturas_sii_recibidas.
 * @param {number}   proveedorId - proveedor_id de la factura.
 * @param {string}   pais        - país de la factura ("Chile" | "Perú").
 */
const AsociarPagosCapexModal = ({ show, onClose, onSuccess, facturaId, proveedorId, pais }) => {
  const { formatAmount } = useCurrency();
  const { id: userId } = useAuthContext();

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [factura, setFactura] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [seleccion, setSeleccion] = useState(() => new Set()); // Set<movimientoCajaId>
  const [asociacion, setAsociacion] = useState(null); // asociación activa (si ya está asociada)
  const [pagosAsociados, setPagosAsociados] = useState([]); // detalle de los pagos ya asociados

  const cargar = async () => {
    setCargando(true);
    try {
      const [{ data: facturaRow, error: errFac }, asoc] = await Promise.all([
        client
          .from("facturas_sii_recibidas")
          .select("id, folio, monto_neto, monto_iva, monto_total, fecha_emision, razon_social_emisor, proveedor_id")
          .eq("id", facturaId)
          .single(),
        getAsociacionActiva(facturaId),
      ]);
      if (errFac) throw errFac;
      setFactura(facturaRow);
      setAsociacion(asoc);
      if (asoc) {
        // Ya asociada → traer el detalle de los pagos (por sus bancarios) para el panel de reversa.
        const { data: pagos, error: errPagos } = await client
          .from("movimientos_caja")
          .select("id, movimiento_bancario_id, fecha, monto, moneda, descripcion")
          .in("movimiento_bancario_id", asoc.bancario_ids || [])
          .eq("categoria_pago", "compra_activo");
        if (errPagos) throw errPagos;
        setPagosAsociados(pagos || []);
        setCandidatos([]);
      } else {
        const pagos = await getPagosCapexCandidatos(pais);
        setCandidatos(pagos);
        setPagosAsociados([]);
      }
      setSeleccion(new Set());
    } catch (e) {
      toast.error(`Error al cargar: ${e.message || e}`);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    let vivo = true;
    (async () => {
      if (vivo) await cargar();
    })();
    return () => {
      vivo = false;
    };
    // Se monta fresco en cada apertura (render condicional en el padre).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturaId, pais]);

  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";
  const total = Number(factura?.monto_total || 0);
  const neto = Number(factura?.monto_neto || 0);

  const toggle = (id) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sumaSeleccion = useMemo(
    () => candidatos.filter((c) => seleccion.has(c.movimientoCajaId)).reduce((s, c) => s + c.monto, 0),
    [candidatos, seleccion]
  );

  const diferencia = sumaSeleccion - total;
  const cuadra = seleccion.size > 0 && Math.abs(diferencia) <= TOLERANCIA_CUADRE_LOCAL;
  const supera = diferencia > TOLERANCIA_CUADRE_LOCAL;
  // Cubre el total dentro de la tolerancia (cuadra o sobre-transfiere) → la factura quedará Pagada.
  const cubre = seleccion.size > 0 && sumaSeleccion >= total - TOLERANCIA_CUADRE_LOCAL;
  const colorCuadre = seleccion.size === 0 ? "#666" : cuadra ? "#2e7d32" : supera ? "#b7791f" : "#c62828";

  const handleConfirmar = async () => {
    const bancarioIds = candidatos.filter((c) => seleccion.has(c.movimientoCajaId)).map((c) => c.movimientoBancarioId);
    if (bancarioIds.length === 0) return;
    setGuardando(true);
    try {
      await asociarPagosCapex(facturaId, bancarioIds, userId);
      toast.success("Pagos de CapEx asociados · factura pagada");
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(`No se pudo asociar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  const handleDesasociar = async () => {
    if (
      !window.confirm(
        "¿Desasociar los pagos de CapEx de esta factura? Vuelve a quedar impaga y los activos a planificado."
      )
    ) {
      return;
    }
    setGuardando(true);
    try {
      await desasociarPagosCapex(facturaId, userId);
      toast.success("Asociación revertida");
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(`No se pudo desasociar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  const yaAsociada = !!asociacion;
  const sumaAsociados = pagosAsociados.reduce((s, p) => s + Number(p.monto || 0), 0);

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          {yaAsociada ? "Pagos de CapEx asociados a esta factura" : "Asociar pagos de CapEx a esta factura"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Header de la factura */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: 10,
            rowGap: 2,
            fontSize: "0.9rem",
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 12px",
            marginBottom: 12,
          }}
        >
          <strong>Factura:</strong>
          <span>
            {factura?.folio ? `Folio ${factura.folio}` : "—"}
            {factura?.razon_social_emisor ? ` · ${factura.razon_social_emisor}` : ""}
          </span>
          <strong>Emisión:</strong>
          <span>{factura?.fecha_emision || "—"}</span>
          <strong>Neto / Total:</strong>
          <span>
            {formatAmount(neto, monedaLocal)} · <strong>{formatAmount(total, monedaLocal)}</strong>
          </span>
        </div>

        {cargando ? (
          <div style={{ textAlign: "center", padding: 30, color: "#888" }}>
            <Spinner animation="border" size="sm" /> Cargando…
          </div>
        ) : yaAsociada ? (
          <>
            <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
              Esta factura ya tiene {pagosAsociados.length} pago(s) de CapEx asociados (
              {(asociacion.activo_ids || []).length} activo(s) ligados). Desasociar revierte todo: la factura vuelve a
              impaga, los pagos a huérfanos y los activos a planificado.
            </div>
            <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #dee2e6", textAlign: "left", color: "#666" }}>
                  <th style={{ padding: "6px 4px" }}>Fecha</th>
                  <th style={{ padding: "6px 4px", textAlign: "right" }}>Monto</th>
                  <th style={{ padding: "6px 4px" }}>Descripción</th>
                </tr>
              </thead>
              <tbody>
                {pagosAsociados.map((p) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{p.fecha}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                      {formatAmount(Number(p.monto), p.moneda || monedaLocal)}
                    </td>
                    <td style={{ padding: "6px 4px", color: "#444" }}>{p.descripcion || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : candidatos.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: "#888" }}>
            No hay pagos de CapEx sin asociar en {pais}.
          </div>
        ) : (
          <>
            <div style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
              Pagos de CapEx sin factura asociada. Tildá los que correspondan a esta factura (el sistema liga los
              activos y arranca la depreciación bajo el agua al confirmar).
            </div>
            <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #dee2e6", textAlign: "left", color: "#666" }}>
                  <th style={{ width: 34 }} />
                  <th style={{ padding: "6px 4px" }}>Fecha</th>
                  <th style={{ padding: "6px 4px", textAlign: "right" }}>Monto</th>
                  <th style={{ padding: "6px 4px" }}>Qué compró</th>
                </tr>
              </thead>
              <tbody>
                {candidatos.map((c) => {
                  const marcado = seleccion.has(c.movimientoCajaId);
                  return (
                    <tr
                      key={c.movimientoCajaId}
                      onClick={() => toggle(c.movimientoCajaId)}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        cursor: "pointer",
                        background: marcado ? "#e8f4fd" : undefined,
                      }}
                    >
                      <td style={{ textAlign: "center", padding: "6px 4px" }}>
                        {/* El toggle lo hace el onClick del <tr> (cubre toda la fila, incluido el
                            casillero por burbujeo). El checkbox solo REFLEJA el estado (readOnly): si
                            además tuviera onChange, un click en el casillero dispararía DOS toggles
                            (change + click que burbujea) y se cancelarían → nunca quedaba tildado. */}
                        <input type="checkbox" checked={marcado} readOnly />
                      </td>
                      <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{c.fecha}</td>
                      <td style={{ padding: "6px 4px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                        {formatAmount(c.monto, c.moneda || monedaLocal)}
                      </td>
                      <td style={{ padding: "6px 4px", color: "#444" }}>
                        {c.contexto.map((ctx) => `${ctx.edificioNombre} · ${ctx.tipoActivo}`).join(" — ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        {yaAsociada ? (
          <>
            <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#555" }}>
              Σ asociado: {formatAmount(sumaAsociados, monedaLocal)} / {formatAmount(total, monedaLocal)}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={onClose} disabled={guardando}>
                Cerrar
              </Button>
              <Button variant="danger" size="sm" onClick={handleDesasociar} disabled={guardando}>
                {guardando ? "Desasociando…" : "Desasociar"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: "0.9rem", fontWeight: 600, color: colorCuadre }}>
              Σ seleccionado: {formatAmount(sumaSeleccion, monedaLocal)} / {formatAmount(total, monedaLocal)}
              {seleccion.size > 0 && (
                <span style={{ marginLeft: 8, fontWeight: 400 }}>
                  {cuadra
                    ? "✓ cuadra"
                    : supera
                      ? `supera por ${formatAmount(diferencia, monedaLocal)}`
                      : `faltan ${formatAmount(-diferencia, monedaLocal)}`}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={onClose} disabled={guardando}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmar}
                disabled={guardando || !cubre}
                title={!cubre ? "Seleccioná pagos que cubran el total de la factura" : undefined}
              >
                {guardando ? "Asociando…" : "Confirmar asociación"}
              </Button>
            </div>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AsociarPagosCapexModal;
